import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Clock, Users, BarChart, ArrowRight, Check, Info, AlertTriangle } from 'lucide-react';

// Define plans outside component to avoid dependency issues
const PLANS = {
  starter: { price: 29, searches: 500, portals: 1, name: 'Starter' },
  professional: { price: 79, searches: 2500, portals: 5, name: 'Professional' },
  business: { price: 199, searches: 10000, portals: 'Unlimited', name: 'Business' },
  enterprise: { price: 'Custom', searches: 'Unlimited', portals: 'Unlimited', name: 'Enterprise' }
};

export default function ROICalculator() {
  // Default values
  const [employeeCost, setEmployeeCost] = useState(30);
  const [teamSize, setTeamSize] = useState(3);
  const [clientCount, setClientCount] = useState(25);
  const [statusUpdates, setStatusUpdates] = useState(10);
  const [updateTime, setUpdateTime] = useState(5);
  const [plan, setPlan] = useState('starter');
  const [monthlyReduction, setMonthlyReduction] = useState(70);
  const [isAnnual, setIsAnnual] = useState(true);
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [estimatedSearches, setEstimatedSearches] = useState(0);
  const [planWarning, setPlanWarning] = useState(false);

  // Calculated results
  const [results, setResults] = useState({
    timeSpent: 0,
    timeSaved: 0,
    costSaved: 0,
    roi: 0,
    payback: 0
  });

  // Calculate estimated search volume
  useEffect(() => {
    // Estimate total monthly searches (clients × status updates × clients who will use the portal)
    const estimated = clientCount * statusUpdates * 0.8; // Assuming 80% adoption rate
    setEstimatedSearches(Math.round(estimated));

    // Auto-suggest plan based on search volume
    if (estimated <= 500) {
      setPlan('starter');
    } else if (estimated <= 2500) {
      setPlan('professional');
    } else if (estimated <= 10000) {
      setPlan('business');
    } else {
      setShowEnterprise(true);
      setPlan('business'); // Default to business plan if enterprise not selected
    }
  }, [clientCount, statusUpdates]);

  // Check if selected plan can handle the search volume
  useEffect(() => {
    if (plan !== 'enterprise' && PLANS[plan].searches !== 'Unlimited' && estimatedSearches > PLANS[plan].searches) {
      setPlanWarning(true);
    } else {
      setPlanWarning(false);
    }
  }, [plan, estimatedSearches]);

  // Calculate ROI
  useEffect(() => {
    // Time spent on status updates per month (in hours) - across entire team
    const timeSpent = (clientCount * statusUpdates * updateTime) / 60 * teamSize;

    // Time saved per month (in hours)
    const timeSaved = timeSpent * (monthlyReduction / 100);

    // Cost saved per month (in dollars)
    const costSaved = timeSaved * employeeCost;

    // Monthly plan cost
    const planCost = plan === 'enterprise' 
      ? estimatedSearches > 20000 ? 499 : 299 // Estimated enterprise pricing based on volume
      : (isAnnual ? PLANS[plan].price * 0.8 : PLANS[plan].price);

    // ROI calculation: (Benefits - Costs) / Costs * 100
    const roi = ((costSaved - planCost) / planCost) * 100;

    // Payback period in months: Cost / Monthly Benefit
    const payback = planCost / costSaved;

    setResults({
      timeSpent: Math.round(timeSpent * 10) / 10,
      timeSaved: Math.round(timeSaved * 10) / 10,
      costSaved: Math.round(costSaved),
      roi: Math.round(roi),
      payback: Math.round(payback * 10) / 10
    });
  }, [employeeCost, teamSize, clientCount, statusUpdates, updateTime, plan, monthlyReduction, isAnnual, estimatedSearches]);

  // Helper function to format currency
  const formatCurrency = (value) => {
    if (typeof value !== 'number') return value;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Helper function to format large numbers
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden transition-colors">
      <div className="p-6 bg-blue-600 dark:bg-blue-700 text-white">
        <div className="flex items-center space-x-3">
          <Calculator className="h-8 w-8" />
          <h2 className="text-2xl font-bold">ROI Calculator</h2>
        </div>
        <p className="mt-2 text-blue-100">Estimate your potential savings with TrackerSheets</p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              Employee Hourly Cost ({formatCurrency(employeeCost)}/hr)
              <div className="relative ml-1 group">
                <Info className="h-4 w-4 text-gray-400" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                  Include salary, benefits, taxes, and overhead costs when estimating the true hourly cost of your staff.
                </div>
              </div>
            </label>
            <input
              type="range"
              min="15"
              max="100"
              value={employeeCost}
              onChange={(e) => setEmployeeCost(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>${15}/hr</span>
              <span>${100}/hr</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              Team Size ({teamSize} people)
              <div className="relative ml-1 group">
                <Info className="h-4 w-4 text-gray-400" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                  The number of team members who handle client status updates and inquiries.
                </div>
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="50"
              value={teamSize}
              onChange={(e) => setTeamSize(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1 person</span>
              <span>50 people</span>
            </div>
            <div className="mt-2 flex items-center">
              <input
                type="number"
                min="1" 
                max="500"
                value={teamSize}
                onChange={(e) => setTeamSize(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-slate-700"
              />
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                For larger teams (enter up to 500)
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              Number of Active Clients ({clientCount})
              <div className="relative ml-1 group">
                <Info className="h-4 w-4 text-gray-400" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                  The number of clients you are actively working with who regularly request status updates.
                </div>
              </div>
            </label>
            <input
              type="range"
              min="5"
              max="500"
              value={clientCount}
              onChange={(e) => setClientCount(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>5</span>
              <span>500</span>
            </div>
            <div className="mt-2 flex items-center">
              <input
                type="number"
                min="5" 
                max="5000"
                value={clientCount}
                onChange={(e) => setClientCount(Math.min(5000, Math.max(5, parseInt(e.target.value) || 5)))}
                className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-slate-700"
              />
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                For large businesses (enter up to 5000)
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              Status Updates Per Client Per Month ({statusUpdates})
              <div className="relative ml-1 group">
                <Info className="h-4 w-4 text-gray-400" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                  How often clients typically ask for updates about their project/case/order status in a month.
                </div>
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="30"
              value={statusUpdates}
              onChange={(e) => setStatusUpdates(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1</span>
              <span>30</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              Average Update Time ({updateTime} minutes)
              <div className="relative ml-1 group">
                <Info className="h-4 w-4 text-gray-400" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                  The average time it takes to respond to a status request, including finding information and communicating with the client.
                </div>
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="15"
              value={updateTime}
              onChange={(e) => setUpdateTime(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1 min</span>
              <span>15 min</span>
            </div>
          </div>
        </div>

        {/* Search volume estimate display */}
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Estimated Monthly Search Volume:
                </span>
              </div>
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {formatNumber(estimatedSearches)} searches
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Team Size:
                </span>
              </div>
              <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {teamSize} {teamSize === 1 ? 'person' : 'people'}
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Search volume based on {clientCount} clients × {statusUpdates} status updates × 80% portal adoption
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select a Plan <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(auto-suggested based on estimated search volume)</span>
          </label>
          
          {planWarning && (
            <div className="mb-4 flex items-start p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Warning: Plan capacity exceeded</p>
                <p className="text-xs mt-1">Your estimated search volume ({formatNumber(estimatedSearches)}/month) exceeds the capacity of the selected plan ({formatNumber(PLANS[plan].searches)}/month). Please select a higher tier plan.</p>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(PLANS).map(([planId, planData]) => {
              // Skip enterprise plan if not showing it
              if (planId === 'enterprise' && !showEnterprise) return null;
              
              const isSelectedPlan = plan === planId;
              const isPlanExceeded = planId !== 'enterprise' && 
                planData.searches !== 'Unlimited' && 
                estimatedSearches > planData.searches;
              
              return (
                <div 
                  key={planId}
                  onClick={() => setPlan(planId)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    isSelectedPlan 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
                  } ${isPlanExceeded ? 'border-yellow-400 dark:border-yellow-600' : ''}`}
                >
                  <div className="font-medium text-gray-900 dark:text-white flex items-center justify-between">
                    {planData.name}
                    {isPlanExceeded && (
                      <AlertTriangle className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
                    )}
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {planId === 'enterprise' ? 'Custom' : (isAnnual ? formatCurrency(planData.price * 0.8) : formatCurrency(planData.price))}
                    <span className="text-xs text-gray-500 dark:text-gray-400">/mo</span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {typeof planData.searches === 'number' 
                      ? `${formatNumber(planData.searches)} searches/mo` 
                      : planData.searches + ' searches'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {typeof planData.portals === 'number' 
                      ? `Up to ${planData.portals} portal${planData.portals > 1 ? 's' : ''}` 
                      : planData.portals + ' portals'}
                  </div>
                </div>
              );
            })}
          </div>

          {estimatedSearches > 10000 && !showEnterprise && (
            <div className="mt-3 text-sm text-blue-600 dark:text-blue-400 cursor-pointer" onClick={() => setShowEnterprise(true)}>
              + Show Enterprise options for high volume needs
            </div>
          )}

          <div className="mt-3 flex items-center justify-end">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={() => setIsAnnual(!isAnnual)}
                className="sr-only"
              />
              <div className={`relative w-10 h-5 transition-colors rounded-full ${isAnnual ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <div className={`absolute left-0.5 top-0.5 w-4 h-4 transition-transform bg-white rounded-full ${isAnnual ? 'translate-x-5' : ''}`}></div>
              </div>
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Annual billing (20% off)</span>
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            Expected Reduction in Status Updates ({monthlyReduction}%)
            <div className="relative ml-1 group">
              <Info className="h-4 w-4 text-gray-400" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 bg-gray-800 text-white text-xs rounded p-2 hidden group-hover:block shadow-lg z-10">
                Based on customer data, most businesses see a 65-80% reduction in status-related calls and emails after implementing TrackerSheets.
              </div>
            </div>
          </label>
          <input
            type="range"
            min="50"
            max="90"
            value={monthlyReduction}
            onChange={(e) => setMonthlyReduction(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>50% reduction</span>
            <span>90% reduction</span>
          </div>
        </div>

        {/* Results section */}
        <div className="mt-8 bg-blue-50 dark:bg-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Your Monthly ROI</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
              <div className="flex items-center text-blue-600 dark:text-blue-400 mb-1">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Team Time Spent Now</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {results.timeSpent} hours
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                per month across {teamSize} {teamSize === 1 ? 'person' : 'people'}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
              <div className="flex items-center text-green-600 dark:text-green-400 mb-1">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Team Time Saved</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {results.timeSaved} hours
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                per month ({Math.round(results.timeSaved/teamSize * 10) / 10} hrs per person)
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
              <div className="flex items-center text-green-600 dark:text-green-400 mb-1">
                <DollarSign className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Cost Savings</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(results.costSaved)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">per month</div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
              <div className="flex items-center text-purple-600 dark:text-purple-400 mb-1">
                <BarChart className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">ROI</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {results.roi}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">monthly return</div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Payback Period</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Your investment pays for itself in:
                </div>
              </div>
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {results.payback < 1 ? 'Less than 1 month' : `${results.payback} months`}
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 p-4 rounded-lg">
            <h4 className="font-medium text-green-800 dark:text-green-300 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Annual Savings Summary
            </h4>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Team Time Saved</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {Math.round(results.timeSaved * 12)} hours/year
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {Math.round(results.timeSaved * 12 / teamSize)} hours per employee
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Cost Savings</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(results.costSaved * 12)}/year
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Net Benefit</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {plan === 'enterprise' 
                    ? 'Contact sales' 
                    : formatCurrency(results.costSaved * 12 - (isAnnual ? PLANS[plan].price * 0.8 * 12 : PLANS[plan].price * 12))}/year
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          {plan === 'enterprise' ? (
            <button className="px-8 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded-lg shadow-md transition-colors flex items-center mx-auto">
              Contact Sales for Enterprise Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          ) : (
            <button className="px-8 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded-lg shadow-md transition-colors flex items-center mx-auto">
              Start Your Free Trial Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          )}
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {plan === 'enterprise' ? 'Custom pricing tailored to your business needs.' : 'No credit card required. 14-day free trial.'}
          </p>
        </div>
      </div>
    </div>
  );
}